/**
 * Slash chord recognition — "Which slash chord is this?"
 *
 * A slash chord names a triad-and-bass pair: "F/G" = F major triad over
 * G in the bass. When the bass is one of the triad's chord tones, the
 * notation is functionally an inversion (G/B = G major in 1st inversion).
 * When the bass is a non-chord-tone, it's a "true" slash chord with a
 * sus/dom-7-flavored color.
 *
 * The generator handles both cases — that's intentional. Slash-chord
 * notation in real music covers both, and the curriculum's `slash-chords`
 * node mixes them so the user learns the *notation* + the *sound*
 * regardless of whether it's an inversion underneath. The chord-inversion
 * exercises stay focused on the inversion-grading task; this one
 * specifically asks "which slash chord did you hear" by name.
 *
 * Audio: bass alone (so the user hears the foundation), triad arpeggio
 * above, then block chord. Distractors come from a small pool of common
 * slash-chord shapes.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import { buildChord } from "../utils.js";

/** Letter+accidental → semitone offset from C. */
const NOTE_TO_SEMI: Record<string, number> = {
  C: 0, "C#": 1, "Db": 1, D: 2, "D#": 3, "Eb": 3,
  E: 4, F: 5, "F#": 6, "Gb": 6, G: 7, "G#": 8, "Ab": 8,
  A: 9, "A#": 10, "Bb": 10, B: 11,
};

export interface SlashChordParams {
  /** Letter (+ optional accidental) of the upper triad's root, e.g. "F" or "F#". */
  upperRoot: string;
  /** Quality of the upper chord — almost always a triad in this curriculum. */
  upperQuality: string;
  /** Letter (+ optional accidental) of the bass note, e.g. "G" or "Bb". */
  bass: string;
}

/**
 * Pool of slash chords used both as items and as distractors. Keep it
 * small and recognizable — these are the shapes that show up most in
 * pop / rock / folk / jazz lead sheets.
 */
const SLASH_CHORD_POOL: Array<{ upperRoot: string; upperQuality: string; bass: string }> = [
  { upperRoot: "G", upperQuality: "maj", bass: "B" },   // G/B — 1st inversion of G
  { upperRoot: "F", upperQuality: "maj", bass: "C" },   // F/C — 2nd inversion of F
  { upperRoot: "C", upperQuality: "maj", bass: "E" },   // C/E — 1st inversion of C
  { upperRoot: "F", upperQuality: "maj", bass: "G" },   // F/G — non-chord-tone bass (sus-flavored)
  { upperRoot: "C", upperQuality: "maj", bass: "G" },   // C/G — 2nd inversion of C
  { upperRoot: "D", upperQuality: "min", bass: "F" },   // Dm/F — 1st inversion of Dm
];

function slashLabel(p: { upperRoot: string; upperQuality: string; bass: string }): string {
  const upperSym = p.upperQuality === "min" ? `${p.upperRoot}m` : p.upperRoot;
  return `${upperSym}/${p.bass}`;
}

function paramsKey(p: { upperRoot: string; upperQuality: string; bass: string }): string {
  return `${p.upperRoot}-${p.upperQuality}-${p.bass}`;
}

export function generateSlashChord(
  itemId: string,
  params: SlashChordParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);

  const upperRootSemi = NOTE_TO_SEMI[params.upperRoot];
  const bassSemi = NOTE_TO_SEMI[params.bass];
  if (upperRootSemi === undefined) {
    throw new Error(`Unknown upper-root note: ${params.upperRoot}`);
  }
  if (bassSemi === undefined) {
    throw new Error(`Unknown bass note: ${params.bass}`);
  }

  // Place the upper triad in the C4 area (root in [60..71]); place the bass
  // in the octave below (≤ root). This keeps the slash chord at a fixed
  // pitch level so the user is always identifying by ear-shape, not by
  // tracking key-context shifts.
  const upperRootMidi = 60 + upperRootSemi;
  // Bass: take the same pitch class in the octave at-or-below the upper root.
  let bassMidi = 48 + bassSemi;
  if (bassMidi >= upperRootMidi) bassMidi -= 12;

  const triad = buildChord(upperRootMidi, params.upperQuality);

  const builder = new AudioPlanBuilder();
  // Bass note alone — establishes the foundation (longer hold so it lands).
  builder.addNote(bassMidi, 0.9, 0.24, "bass");
  builder.advance(0.95);
  // Triad arpeggio (low → high) above the bass.
  for (const note of triad) {
    builder.addNote(note, 0.55, 0.20, "melody");
    builder.advance(0.30);
  }
  builder.advance(0.4);
  // Block: bass + triad simultaneously.
  builder.addNote(bassMidi, 1.6, 0.20, "bass");
  builder.addChord(triad, 1.6, 0.13, "harmony");
  builder.advance(1.7);
  const audio = builder.build();

  // Build options: target + 3 distractors from the pool.
  const targetKey = paramsKey(params);
  const pool = SLASH_CHORD_POOL.filter((p) => paramsKey(p) !== targetKey);
  const distractors = rng.sample(pool, 3);
  const all = rng.shuffle([params, ...distractors]);

  const options: AnswerOption[] = all.map((p) => ({
    id: paramsKey(p),
    label: slashLabel(p),
    sublabel: p === params ? undefined : undefined,
  }));

  const correctId = paramsKey(params);
  const targetLabel = slashLabel(params);

  return {
    itemId,
    exerciseType: "slash-chord-recognition",
    audio,
    prompt: "Which slash chord did you hear?",
    options,
    correctAnswerId: correctId,
    meta: {
      seed,
      explanation: `${targetLabel}: ${params.upperRoot}${params.upperQuality === "min" ? " minor" : " major"} triad over ${params.bass} in the bass.`,
      distractorCount: options.length - 1,
      teachAudio: buildSlashChordTeachPlan(bassMidi, triad),
    },
  };
}

/**
 * Wrong-answer teach plan: bass alone (longer), then triad alone, then
 * block. Separating bass from triad makes the slash relationship obvious
 * — you hear the foundation, then the chord above, then how they
 * combine.
 */
function buildSlashChordTeachPlan(bassMidi: number, triad: number[]): AudioPlan {
  const builder = new AudioPlanBuilder();
  // Bass alone — longer hold so it really registers.
  builder.addNote(bassMidi, 1.4, 0.26, "target");
  builder.advance(1.6);
  // Triad arpeggio without the bass.
  for (const note of triad) {
    builder.addNote(note, 0.7, 0.22, "melody");
    builder.advance(0.45);
  }
  builder.advance(0.4);
  // Block: bass + triad together.
  builder.addNote(bassMidi, 1.8, 0.20, "bass");
  builder.addChord(triad, 1.8, 0.13, "harmony");
  builder.advance(1.9);
  return builder.build();
}
